// src/pages/Appli/AppliAdmin/ListingClients/components/AssociationQuickViewModal.jsx
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaStar, FaGlobe, FaMapMarkerAlt } from "react-icons/fa";
import DOMPurify from "dompurify";
import ButtonSite from "../../../../../components/Button/ButtonSite";
import ButtonTel from "../../../../../components/Button/ButtonTel";
import ButtonSMS from "../../../../../components/Button/ButtonSMS";
import ButtonMail from "../../../../../components/Button/ButtonMail";

const fallbackImg = "/images/no-image.jpg";

const tones = {
  Prospect: "bg-blue-100 text-blue-700",
  "En Test": "bg-yellow-100 text-yellow-700",
  "Devis Signé": "bg-amber-100 text-amber-700",
  Client: "bg-green-100 text-green-700",
  "Client SAAS": "bg-green-200 text-green-800",
  Professionnel: "bg-purple-100 text-purple-700",
  Perdu: "bg-red-100 text-red-700",
};

export function AssociationQuickViewModal({ association, onClose }) {
  const dialogRef = useRef(null);
  const isOpen = !!association; // <- clé : pas de return avant les hooks

  // Scroll lock + ESC + focus (ne s'exécute que si la modale est ouverte)
  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("button[data-close]")?.focus();
    }, 0);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  if (!isOpen) return null; // <- le return arrive après les hooks

  const statutClass =
    tones[association.statut_asso] || "bg-slate-100 text-slate-700";

  const mapsHref =
    association.latitude && association.longitude
      ? `https://www.google.com/maps?q=${encodeURIComponent(
          `${association.latitude},${association.longitude}`
        )}`
      : association.adresse_asso
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          association.adresse_asso
        )}`
      : null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Aperçu Association"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight truncate">
              Fiche Association
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Vue rapide des informations du club / client.
            </p>
          </div>
          <button
            data-close
            onClick={onClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body scrollable */}
        <div
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-[280px,1fr] gap-5 sm:gap-6">
            {/* Colonne gauche : identité + actions */}
            <div className="flex flex-col items-stretch gap-4">
              <div className="relative w-full">
                <div className="relative h-32 sm:h-36 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center shadow">
                  <img
                    src={association.logo_asso || fallbackImg}
                    alt="logo"
                    onError={(e) => {
                      e.currentTarget.src = fallbackImg;
                    }}
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>

              <div className="text-center">
                <div className="font-semibold text-lg break-words">
                  {association.nom_asso || "—"}
                </div>
                <div className="mt-1 flex items-center justify-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-xs ${statutClass}`}>
                    {association.statut_asso || "—"}
                  </span>
                  {association.is_favorite ? (
                    <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                      <FaStar className="opacity-80" /> Favori
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Boutons contact / site */}
              <div className="mt-1 flex flex-wrap gap-2 justify-center">
                {association.siteweb_asso && (
                  <ButtonSite href={association.siteweb_asso} target="_blank" />
                )}
                {association.tel_asso && <ButtonTel tel={association.tel_asso} />}
                {association.tel_asso && <ButtonSMS tel={association.tel_asso} />}
                {association.mail_asso && <ButtonMail mail={association.mail_asso} />}
              </div>

              {/* Adresse + lien Maps + API link */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <div className="text-xs text-slate-500 mb-1">Coordonnées</div>
                <div className="space-y-1 text-sm">
                  <div className="flex items-start gap-2">
                    <FaMapMarkerAlt className="mt-0.5 opacity-70" />
                    <span className="break-words">
                      {association.adresse_asso || "—"}
                    </span>
                  </div>
                  {/* <div className="flex items-start gap-2">
                    <FaGlobe className="mt-0.5 opacity-70" />
                    <span className="break-all">
                      {association.apiUrl_asso ? (
                        <a
                          href={association.apiUrl_asso}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-blue-600"
                        >
                          {association.apiUrl_asso}
                        </a>
                      ) : (
                        "API —"
                      )}
                    </span>
                  </div> */}
                  {(association.latitude && association.longitude) || association.adresse_asso ? (
                    <div className="pt-2">
                      <a
                        href={
                          association.latitude && association.longitude
                            ? `https://www.google.com/maps?q=${encodeURIComponent(
                                `${association.latitude},${association.longitude}`
                              )}`
                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                association.adresse_asso
                              )}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs underline text-blue-600"
                      >
                        Ouvrir dans Google Maps ↗
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Colonne droite : infos structurées */}
            <div className="grid grid-cols-1 gap-4">
              <InfoRow label="Produit" value={association.produit_asso || "—"} />
              <InfoRow
                label="Discipline(s)"
                value={association.discipline_asso || "—"}
              />

              {/* Détail riche */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <div className="text-xs text-slate-500">Détail</div>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none mt-1"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(association.detail_asso || ""),
                  }}
                />
              </div>

              {/* Contacts associés */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <div className="text-xs text-slate-500 mb-1">
                  Contacts associés
                </div>
                {association.prospects?.length ? (
                  <ul className="list-disc list-inside text-sm">
                    {association.prospects.map((p) => (
                      <li key={p.id}>
                        <span className="font-semibold">{p.nom}</span>{" "}
                        {p.prenom}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-slate-400 text-sm italic">
                    Aucun contact
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Styles locaux */}
        <style>{`
          .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
          .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
            background: rgba(148,163,184,.5);
            border-radius: 9999px;
            border: 3px solid transparent;
            background-clip: content-box;
          }
          .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb { background: rgba(148,163,184,.35); }
          .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
        `}</style>
      </div>
    </div>,
    document.body
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm mt-0.5 break-words">{value}</div>
    </div>
  );
}

export default AssociationQuickViewModal;
